import React, { useState } from 'react';
import { FaTrophy, FaLayerGroup, FaCompress, FaInfoCircle, FaChevronRight } from 'react-icons/fa';
import { Lock } from 'lucide-react';
import { usePremium } from '../Subscription/PremiumProvider';
import PremiumUpgradeModal from '../Subscription/PremiumUpgradeModal';

const CircularProgress = ({ value, total, color = 'blue' }) => {
  const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
  const strokeWidth = 4;
  const size = 40;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  const colorClasses = {
    blue: {
      bg: 'text-blue-100',
      progress: 'text-blue-600',
      text: 'text-blue-700'
    },
    emerald: {
      bg: 'text-emerald-100',
      progress: 'text-emerald-600',
      text: 'text-emerald-700'
    },
    purple: {
      bg: 'text-purple-100',
      progress: 'text-purple-600',
      text: 'text-purple-700'
    },
    amber: {
      bg: 'text-amber-100',
      progress: 'text-amber-600',
      text: 'text-amber-700'
    }
  };

  const colors = colorClasses[color];

  return (
    <div className="relative w-10 h-10 flex items-center justify-center">
      <svg className="transform -rotate-90 w-10 h-10">
        {/* Background circle */}
        <circle
          className={colors.bg}
          strokeWidth={strokeWidth}
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        {/* Progress circle */}
        <circle
          className={`${colors.progress} transition-all duration-300 ease-in-out`}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`text-xs font-semibold ${colors.text}`}>{percentage}%</span>
      </div>
    </div>
  );
};

const WorkspaceStats = ({ baskets, topics }) => {
  const { isPremium } = usePremium();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Calculate mastered baskets (where all topics in the basket are mastered)
  const masteredBaskets = baskets.filter(basket => {
    return basket.topics?.length > 0 && 
           basket.topics.every(topic => topic.is_master);
  }).length;

  // Calculate mastered topics using is_master property
  const masteredTopics = topics.filter(topic => topic.is_master).length;

  // Calculate volume reduction and grouping metrics
  const nonEmptyBaskets = baskets.filter(basket => basket.topics?.length > 0);
  const totalTopics = topics.length;
  const groupedTopics = nonEmptyBaskets.reduce((sum, basket) => 
    sum + (basket.topics?.length || 0), 0);
  
  // Final Total Items = remaining ungrouped topics + number of baskets
  const ungroupedTopics = topics.length - groupedTopics;  // remaining unreduced topics
  const finalTotalItems = ungroupedTopics + nonEmptyBaskets.length;  // Final Total Items
  
  // New volume reduction formula:
  // ((Original Total Topics - Final Total Items) / Original Total Topics) × 100
  const volumeReduction = totalTopics > 0 ? 
    Math.round(((totalTopics - finalTotalItems) / totalTopics) * 100) : 0;

  // Grouping progress
  const groupingProgress = totalTopics > 0 ?
    Math.round((groupedTopics / totalTopics) * 100) : 0;

  return (
    <div className="relative w-full">
      <div className="overflow-x-auto pb-4 hide-scrollbar">
        <div className="flex items-center min-w-max px-4 md:px-0 md:justify-center gap-8">
          {/* Volume Reduction - Premium Feature (Now First) */}
          <div className="relative">
            <div 
              className="flex items-center gap-4"
              onClick={() => !isPremium() && setShowUpgradeModal(true)}
              style={{ cursor: !isPremium() ? 'pointer' : 'default' }}
            >
              {/* Circular Progress with Lock Overlay */}
              <div className="relative">
                <div className={!isPremium() ? 'blur-sm' : ''}>
                  <CircularProgress value={volumeReduction} total={100} color="purple" />
                </div>
                {/* Lock icon overlay on the circle */}
                {!isPremium() && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-full">
                    <Lock className="w-5 h-5 text-blue-500" />
                  </div>
                )}
              </div>

              <div className="relative flex flex-col">
                {/* Title and tooltip - Always visible */}
                <span className="text-sm text-neutral-500 font-medium flex items-center gap-1">
                  Volume Reduction
                  <div className="group relative inline-block">
                    <FaInfoCircle className="w-3.5 h-3.5 text-neutral-400 hover:text-neutral-600 cursor-help" />
                    <div className="invisible group-hover:visible absolute left-full ml-2 top-1/2 -translate-y-1/2
                                  px-3 py-2 bg-neutral-900 text-white text-xs rounded-lg w-48 shadow-lg z-50">
                      Percentage reduction in items after grouping topics into baskets
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 
                                    w-2 h-2 bg-neutral-900 transform rotate-45"></div>
                    </div>
                  </div>
                </span>

                {/* Numbers - Blurred for non-premium */}
                <div className={`flex items-center gap-2 text-lg ${!isPremium() ? 'blur-sm' : ''}`}>
                  <span className="font-semibold text-purple-700">{totalTopics}</span>
                  <FaChevronRight className="text-neutral-400 w-3 h-3" />
                  <span className="font-semibold text-purple-700">{finalTotalItems}</span>
                </div>
                <span className={`text-xs text-neutral-500 ${!isPremium() ? 'blur-sm' : ''}`}>
                  less {totalTopics - finalTotalItems} topics
                </span>

                {/* Premium Unlock Message - Overlaid on numbers */}
                {!isPremium() && (
                  <div className="absolute inset-0 flex items-center justify-center mt-6">
                    <div className="bg-white/60 px-3 py-1.5 rounded-lg border-2 border-blue-200">
                      <span className="text-sm font-medium text-blue-600">
                        Unlock Premium
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="w-px h-8 bg-neutral-200" />

          <div className="flex items-center gap-4">
            <CircularProgress value={masteredBaskets} total={baskets.length} color="blue" />
            <div className="flex flex-col">
              <span className="text-sm text-neutral-500 font-medium flex items-center gap-1">
                Baskets Mastery
                <div className="group relative inline-block">
                  <FaInfoCircle className="w-3.5 h-3.5 text-neutral-400 hover:text-neutral-600 cursor-help" />
                  <div className="invisible group-hover:visible absolute left-full ml-2 top-1/2 -translate-y-1/2
                                px-3 py-2 bg-neutral-900 text-white text-xs rounded-lg w-48 shadow-lg z-50">
                    Number of baskets where all topic items are correctly recalled
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 
                                  w-2 h-2 bg-neutral-900 transform rotate-45"></div>
                  </div>
                </div>
              </span>
              <span className="text-lg font-semibold text-blue-700">
                {masteredBaskets}
                <span className="text-neutral-400 font-normal text-base">/{baskets.length}</span>
              </span>
            </div>
          </div>

          <div className="w-px h-8 bg-neutral-200" />

          <div className="flex items-center gap-4">
            <CircularProgress value={masteredTopics} total={topics.length} color="emerald" />
            <div className="flex flex-col">
              <span className="text-sm text-neutral-500 font-medium flex items-center gap-1">
                Topics Mastery
                <div className="group relative inline-block">
                  <FaInfoCircle className="w-3.5 h-3.5 text-neutral-400 hover:text-neutral-600 cursor-help" />
                  <div className="invisible group-hover:visible absolute left-full ml-2 top-1/2 -translate-y-1/2
                                px-3 py-2 bg-neutral-900 text-white text-xs rounded-lg w-48 shadow-lg z-50">
                    Number of topics correctly recalled inside the basket
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 
                                  w-2 h-2 bg-neutral-900 transform rotate-45"></div>
                  </div>
                </div>
              </span>
              <span className="text-lg font-semibold text-emerald-700">
                {masteredTopics}
                <span className="text-neutral-400 font-normal text-base">/{topics.length}</span>
              </span>
            </div>
          </div>

          <div className="w-px h-8 bg-neutral-200" />

          <div className="flex items-center gap-4">
            <CircularProgress value={groupingProgress} total={100} color="amber" />
            <div className="flex flex-col">
              <span className="text-sm text-neutral-500 font-medium flex items-center gap-1">
                Grouping Progress
                <div className="group relative inline-block">
                  <FaInfoCircle className="w-3.5 h-3.5 text-neutral-400 hover:text-neutral-600 cursor-help" />
                  <div className="invisible group-hover:visible absolute left-full ml-2 top-1/2 -translate-y-1/2
                                px-3 py-2 bg-neutral-900 text-white text-xs rounded-lg w-48 shadow-lg z-50">
                    Percentage of topics that have been organized into baskets
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 
                                  w-2 h-2 bg-neutral-900 transform rotate-45"></div>
                  </div>
                </div>
              </span>
              <div className="flex flex-col">
                <span className="text-lg font-semibold text-amber-700">
                  {groupedTopics}
                  <span className="text-neutral-400 font-normal text-base">/{totalTopics}</span>
                </span>
                <span className="text-xs text-neutral-500">
                  {totalTopics - groupedTopics} topics remaining
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add fade effect on mobile */}
      <div className="absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-white to-transparent md:hidden" />

      {/* Premium Upgrade Modal */}
      <PremiumUpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
      />
    </div>
  );
};

// Add this CSS to your global styles or create a new style block
const styles = `
  .hide-scrollbar {
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
  }
  .hide-scrollbar::-webkit-scrollbar {
    display: none;  /* Chrome, Safari and Opera */
  }
`;

export default WorkspaceStats; 